import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Button from '../components/button';
import {
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import translations from './userprofile.i18n';
import * as css from './userprofile.module.scss';
import PanelHeader from '../components/panelheader/panelHeader';
import { useLoader } from '../api/useLoader';
import {
  BrukerrollerDto,
  HttpError,
  MuligeStudiestedDto,
  StudiestedbrukerprofilPutTilgangerInnerDto,
  StudiestedTilgangerDto,
} from '../api/generated/studiested-generated-api';
import { Bruker } from './studiestedUserprofile';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import {
  getMuligStudiestedDybde,
  IndentMuligStudiested,
} from '../components/indentation/indentation';
import Autocomplete from '../components/input/autocomplete';
import Dropdown from '../components/input/dropdown';
import {
  AlpNotificationType,
  NotificationFunctionContext,
} from '../components/notifications/notificationProvider';
import { uuidv4 } from '../util/uuidv4';
import TilgangColumns from './tilgangColumns';
import {
  OnStedHoverCard,
  StedType,
} from '../components/hierarchy/onhoverhierarchyinfo';

export interface ProfilData {
  bruker: Bruker;
  tilganger?: StudiestedTilgangerDto[];
}

const Tilganger: React.FC<
  React.PropsWithChildren<{
    brukerprofil: ProfilData;
    canAdminister: boolean;
    reloadBrukerprofil?: () => void;
  }>
> = ({ brukerprofil, canAdminister, reloadBrukerprofil }) => {
  const { formatMessage } = useIntl();
  const { studiestedApi } = useContext(StudiestedApiContext);
  const { addNotification } = useContext(NotificationFunctionContext);
  const tilganger: StudiestedTilgangerDto[] = brukerprofil.tilganger || [];
  const [endreTilgang, setEndreTilgang] = useState<
    StudiestedTilgangerDto | undefined
  >();
  const [nyTilgang, setNyTilgang] = useState<boolean>(false);

  const labels = {
    tilganger: formatMessage({ id: translations.tilganger.id }),
    tilgangerSubtitle: formatMessage({ id: translations.tilgangerSubtitle.id }),
    tilgangLagret: formatMessage({ id: translations.tilgangLagret.id }),
    tilgangSlettet: formatMessage({ id: translations.tilgangSlettet.id }),
  };

  const saveTilgang = (
    selectedAddStudiestedId: string,
    selectedAddRolle: BrukerrollerDto,
    editTilgang?: StudiestedTilgangerDto,
  ) => {
    const tilgangerToSave = (tilganger || [])
      .filter((tilgang) => tilgang !== editTilgang)
      .map((tilgang) => {
        return {
          studiestedId: tilgang.studiested.id,
          brukerrolle: tilgang.brukerrolle,
        };
      });
    tilgangerToSave.push({
      studiestedId: selectedAddStudiestedId,
      brukerrolle: selectedAddRolle,
    });
    save(tilgangerToSave, labels.tilgangLagret);
  };

  const deleteTilgang = (deletedTilgang: StudiestedTilgangerDto) => {
    const allExceptDeleted = (brukerprofil.tilganger || [])
      .filter((tilgang) => tilgang !== deletedTilgang)
      .map((tilgang) => {
        return {
          studiestedId: tilgang.studiested.id,
          brukerrolle: tilgang.brukerrolle,
        };
      });
    save(allExceptDeleted, labels.tilgangSlettet);
  };

  function save(
    tilgangerToSave: StudiestedbrukerprofilPutTilgangerInnerDto[],
    saveMessage: string,
  ) {
    studiestedApi
      .studiestedbrukerprofilIdPut({
        pathParams: { id: brukerprofil.bruker.id },
        studiestedbrukerprofilPutDto: {
          tilganger: tilgangerToSave,
        },
      })
      .then(() => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: saveMessage,
        });
        reloadBrukerprofil!();
      })
      .catch((err: HttpError) =>
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        }),
      );
  }

  return (
    <div>
      <div className={css.privileges}>
        <PanelHeader
          title={labels.tilganger}
          subtitle={canAdminister ? labels.tilgangerSubtitle : ''}
          className={css.header}
        />

        <TilgangTable
          tilganger={tilganger || []}
          nyTilgang={nyTilgang}
          endreTilgang={endreTilgang}
          canAdminister={canAdminister}
          deleteTilgang={deleteTilgang}
          setEndreTilgang={setEndreTilgang}
          saveTilgang={saveTilgang}
        />

        {canAdminister && (
          <ActionButton
            endreTilgang={endreTilgang}
            setEndreTilgang={setEndreTilgang}
            nyTilgang={nyTilgang}
            setNyTilgang={setNyTilgang}
          />
        )}
      </div>
    </div>
  );
};

const ActionButton: React.FC<
  React.PropsWithChildren<{
    endreTilgang: StudiestedTilgangerDto | undefined;
    setEndreTilgang: (tilgang: StudiestedTilgangerDto | undefined) => void;
    nyTilgang: boolean;
    setNyTilgang: (newMode: boolean) => void;
  }>
> = ({ endreTilgang, setEndreTilgang, nyTilgang, setNyTilgang }) => {
  const { formatMessage } = useIntl();

  const labels = {
    nyTilgang: formatMessage({ id: translations.nyTilgang.id }),
    avbryt: formatMessage({ id: translations.avbryt.id }),
  };
  return (
    <div
      data-cy={'administerAccessButtons'}
      className={css.permissionButtonContainer}
    >
      {!endreTilgang && !nyTilgang && (
        <Button.Secondary onClick={() => setNyTilgang(true)}>
          {labels.nyTilgang}
        </Button.Secondary>
      )}

      {(endreTilgang || nyTilgang) && (
        <Button.Tertiary
          onClick={() => {
            setNyTilgang(false);
            setEndreTilgang(undefined);
          }}
        >
          {labels.avbryt}
        </Button.Tertiary>
      )}
    </div>
  );
};

const TilgangTable: React.FC<
  React.PropsWithChildren<{
    tilganger: StudiestedTilgangerDto[];
    endreTilgang: StudiestedTilgangerDto | undefined;
    nyTilgang: boolean;
    canAdminister: boolean;
    deleteTilgang: (tilgang: StudiestedTilgangerDto) => void;
    setEndreTilgang: (tilgang: StudiestedTilgangerDto) => void;
    saveTilgang: (
      selectedAddStudiestedId: string,
      selectedAddRolle: BrukerrollerDto,
      editTilgang?: StudiestedTilgangerDto,
    ) => void;
  }>
> = ({
  tilganger,
  endreTilgang,
  nyTilgang,
  canAdminister,
  deleteTilgang,
  setEndreTilgang,
  saveTilgang,
}) => {
  const { formatMessage, locale } = useIntl();

  const translateRolle = function (rolle: BrukerrollerDto) {
    return formatMessage({ id: translations[`rolle${rolle}`].id });
  };

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = (
    col: number,
    a: StudiestedTilgangerDto,
    b: StudiestedTilgangerDto,
  ) => {
    switch (col) {
      case 0:
        return compare(a.studiested.navn, b.studiested.navn);
      case 1:
        return compare(
          translateRolle(a.brukerrolle),
          translateRolle(b.brukerrolle),
        );
    }
    return 0;
  };

  const sortedTilgangerByActiveCol = (col: number, ascending: boolean) =>
    [...tilganger]
      .filter((tilgang) => tilgang !== endreTilgang!!)
      .sort((a, b) => {
        return ascending ? sort(col, b, a) : sort(col, a, b);
      });

  const [activeCol, setActiveCol] = useState<number>(0);
  const [sortedTilganger, setSortedTilganger] = useState<
    StudiestedTilgangerDto[]
  >(sortedTilgangerByActiveCol(0, false));

  const labels = {
    endre: formatMessage({ id: translations.endre.id }),
    slett: formatMessage({ id: translations.slett.id }),
  };

  const headers = [
    {
      title: formatMessage({ id: translations.tilgangsnivaa.id }),
      type: TilgangColumns.STED,
    },
    {
      title: formatMessage({ id: translations.brukerrettighet.id }),
      type: TilgangColumns.ROLLE,
    },
    {
      title: formatMessage({ id: translations.handling.id }),
      type: TilgangColumns.HANDLING,
    },
  ];

  const getRows = () =>
    sortedTilganger.map((tilgang) => {
      let tabHeader = 0;

      return (
        <TableRow key={tilgang.studiested.id}>
          <TableCellTd header={headers[tabHeader++].title}>
            <div className={css.stedNavnCell}>
              {tilgang.studiested.navn}
              <OnStedHoverCard
                id={tilgang.studiested.id}
                navn={tilgang.studiested.navn}
                sted={StedType.STUDIESTED}
              />
            </div>
          </TableCellTd>
          <TableCellTd header={headers[tabHeader++].title}>
            {translateRolle(tilgang.brukerrolle)}
          </TableCellTd>
          {canAdminister && (
            <TableCellTd header={headers[tabHeader].title} align="right">
              {tilganger.length > 1 && (
                <Button.Secondary
                  disabled={!tilgang.kanRedigere}
                  onClick={() => deleteTilgang(tilgang)}
                >
                  {labels.slett}
                </Button.Secondary>
              )}
              {tilganger.length === 1 && (
                <Button.Secondary
                  disabled={!tilgang.kanRedigere}
                  onClick={() => setEndreTilgang(tilgang)}
                >
                  {labels.endre}
                </Button.Secondary>
              )}
            </TableCellTd>
          )}
        </TableRow>
      );
    });

  const sortTilganger = (col: number, ascending: boolean) =>
    setSortedTilganger(sortedTilgangerByActiveCol(col, ascending));

  const canSortIndices = [0, 1];

  const getHeaders = () =>
    headers.map(({ title, type }) => {
      if (type === TilgangColumns.HANDLING && !canAdminister)
        return <React.Fragment key={title} />;

      return (
        <TableCellTh
          key={title}
          colIndex={type}
          activeCol={type === activeCol}
          setActiveCol={setActiveCol}
          sort={sortTilganger}
          canSort={canSortIndices.includes(type)}
          align={type === TilgangColumns.HANDLING ? 'right' : 'inherit'}
        >
          {title}
        </TableCellTh>
      );
    });

  return (
    <Table>
      <TableHeader>
        <TableRow top>{getHeaders()}</TableRow>
      </TableHeader>
      <TableMain>
        {/* View existing tilganger */}
        {getRows()}
        {/* Add or edit tilgang */}
        {(endreTilgang || nyTilgang) && (
          <NewTilgang tilgang={endreTilgang} saveTilgang={saveTilgang} />
        )}
      </TableMain>
    </Table>
  );
};

const NewTilgang: React.FC<
  React.PropsWithChildren<{
    tilgang: StudiestedTilgangerDto | undefined;
    saveTilgang: (
      selectedAddStudiestedId: string,
      selectedAddRolle: BrukerrollerDto,
      editTilgang?: StudiestedTilgangerDto,
    ) => void;
  }>
> = ({ tilgang, saveTilgang }) => {
  const [selectedAddStudiestedId, setSelectedAddStudiestedId] = useState('');
  const [selectedAddRolle, setSelectedAddRolle] = useState<
    BrukerrollerDto | ''
  >('');

  useEffect(() => {
    setSelectedAddStudiestedId((tilgang && tilgang.studiested.id) || '');
    setSelectedAddRolle((tilgang && tilgang.brukerrolle) || '');
  }, [tilgang]);

  const { studiestedApi } = useContext(StudiestedApiContext);
  const { data: studiesteder } = useLoader(
    () => studiestedApi.muligeStudiestederGet({}),
    [],
  );

  const save = () => {
    saveTilgang(
      selectedAddStudiestedId,
      selectedAddRolle as BrukerrollerDto,
      tilgang,
    );
  };

  const { formatMessage } = useIntl();
  const labels = {
    lagre: formatMessage({ id: translations.lagre.id }),
  };

  if (!studiesteder) {
    return <></>;
  }

  return (
    <TableRow className={css.editRow}>
      <TableCellTd>
        <StudiestedSelect
          setSelectedAddStudiestedId={setSelectedAddStudiestedId}
          selectedAddStudiestedId={selectedAddStudiestedId}
          studiesteder={studiesteder}
        />
      </TableCellTd>
      <TableCellTd>
        <RolleSelect
          selectedAddRolle={selectedAddRolle}
          setSelectedAddRolle={setSelectedAddRolle}
        />
      </TableCellTd>
      <TableCellTd align="right">
        <Button.Secondary
          onClick={save}
          disabled={!selectedAddRolle || !selectedAddStudiestedId}
        >
          {labels.lagre}
        </Button.Secondary>
      </TableCellTd>
    </TableRow>
  );
};

const StudiestedSelect: React.FC<
  React.PropsWithChildren<{
    setSelectedAddStudiestedId: (id: string) => void;
    studiesteder: MuligeStudiestedDto[];
    selectedAddStudiestedId: string;
  }>
> = ({ setSelectedAddStudiestedId, studiesteder, selectedAddStudiestedId }) => {
  const { formatMessage } = useIntl();
  const labels = {
    velgTilgangsnivaa: formatMessage({ id: translations.velgTilgangsnivaa.id }),
  };

  return (
    <div className={css.tilgangSelect}>
      <Autocomplete
        id={'autocomplete'}
        ariaLabel={'institution-access-autocomplete'}
        options={studiesteder.map((studiested) => ({
          ...studiested,
          dybde: getMuligStudiestedDybde(studiested),
          onHoverType: StedType.STUDIESTED,
        }))}
        onChange={(event: object, value: MuligeStudiestedDto) =>
          setSelectedAddStudiestedId(value ? value.id : '')
        }
        placeholder={labels.velgTilgangsnivaa}
        renderOption={(option) => <IndentMuligStudiested studiested={option} />}
        defaultValue={studiesteder.find(
          (studiested) => studiested.id === selectedAddStudiestedId,
        )}
      />
    </div>
  );
};

const RolleSelect: React.FC<
  React.PropsWithChildren<{
    selectedAddRolle: string;
    setSelectedAddRolle: (rolle: BrukerrollerDto) => void;
  }>
> = ({ selectedAddRolle, setSelectedAddRolle }) => {
  const { formatMessage } = useIntl();
  const labels = {
    velgRolle: formatMessage({ id: translations.velgRolle.id }),
  };

  const translateRolle = (rolle: BrukerrollerDto) => {
    return formatMessage({ id: translations[`rolle${rolle}`].id });
  };

  return (
    <div className={css.rolleSelect}>
      <Dropdown
        id={'rolle-select'}
        ariaLabel={'role-select-dropdown'}
        onChange={(event, value) =>
          setSelectedAddRolle(value.id as BrukerrollerDto)
        }
        options={Object.keys(BrukerrollerDto).map((brukerrolle) => ({
          id: brukerrolle,
          navn: translateRolle(brukerrolle as BrukerrollerDto),
        }))}
        placeholder={labels.velgRolle}
        defaultValue={{
          id: selectedAddRolle,
          navn:
            selectedAddRolle &&
            translateRolle(selectedAddRolle as BrukerrollerDto),
        }}
      />
    </div>
  );
};

export default Tilganger;
